import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  HostBinding,
  HostListener,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { matFormFieldAnimations } from '@angular/material/form-field';
import { Subject, takeUntil } from 'rxjs';

import { FormFieldControlDirective } from './form-field-control.directive';

@Component({
    selector: 'lui-form-field',
    template: `
    <div class="lui-form-field-label-container">
      <label #label>
        <ng-content select="lui-label"></ng-content>
      </label>
      <div class="lui-form-field-tootlip-avatar">
        <ng-content select="lui-avatar[matTooltip]"></ng-content>
      </div>
    </div>
    <ng-content></ng-content>
    <div class="lui-form-field-subscript-wrapper">
      <div
        class="lui-form-field-error-wrapper"
        *ngIf="control.ngControl?.invalid && control.ngControl?.touched"
        [@transitionMessages]="
          control.ngControl?.invalid && control.ngControl?.touched
            ? 'enter'
            : ''
        "
      >
        <ng-content select="mat-error, [matError]"></ng-content>
      </div>
    </div>
  `,
    animations: [matFormFieldAnimations.transitionMessages],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FormFieldComponent implements AfterContentInit {
  @ViewChild('label', { static: true }) labelComponent!: ElementRef;

  @ContentChild(FormFieldControlDirective)
  _formFieldControl!: FormFieldControlDirective<any>;

  /** Gets the current form field control */
  get control(): FormFieldControlDirective<any> {
    return this._explicitFormFieldControl || this._formFieldControl;
  }
  set control(value) {
    this._explicitFormFieldControl = value;
  }

  private _isFocused: boolean | null = null;

  _destroy = new Subject<void>();

  private _explicitFormFieldControl!: FormFieldControlDirective<any>;

  /* Class Bindings */

  @HostBinding('class') modelClass = 'lui-form-field';

  @HostBinding('class.ng-untouched') get isUntouched() {
    return this._shouldForward('untouched');
  }

  @HostBinding('class.ng-touched') get isTouched() {
    return this._shouldForward('touched');
  }

  @HostBinding('class.ng-pristine') get isPristine() {
    return this._shouldForward('pristine');
  }

  @HostBinding('class.ng-dirty') get isDirty() {
    return this._shouldForward('dirty');
  }

  @HostBinding('class.ng-valid') get isValid() {
    return this._shouldForward('valid');
  }

  @HostBinding('class.ng-invalid') get isInvalid() {
    return this._shouldForward('invalid');
  }

  @HostBinding('class.ng-pending') get isPending() {
    return this._shouldForward('pending');
  }

  @HostBinding('class.ng-disabled') get isDisabled() {
    return this._shouldForward('disabled');
  }

  @HostListener('focus') onFocus() {
    // this.cd.markForCheck();
    // this.stateChanges.next();
  }

  @HostListener('blur') onBlur() {
    this.cd.markForCheck();
  }

  constructor(
    private cd: ChangeDetectorRef,
    private readonly renderer: Renderer2
  ) {}

  ngAfterContentInit(): void {
    this._initializeControl();

    if (this.control.required) {
      this.renderer.appendChild(
        this.labelComponent.nativeElement.firstChild,
        this.renderer.createText(' *')
      );
    }
  }

  /** Initializes the registered form field control. */
  private _initializeControl() {
    const control = this.control;

    // if (control.controlType) {
    //   this._elementRef.nativeElement.classList.add(
    //     `mat-mdc-form-field-type-${control.controlType}`
    //   );
    // }

    // Subscribe to changes in the child control state in order to update the form field UI.
    control.stateChanges.subscribe(() => {
      this._updateFocusState();
      // this._syncDescribedByIds();
      this.cd.markForCheck();
    });

    // Run change detection if the value changes.
    if (control.ngControl && control.ngControl.valueChanges) {
      control.ngControl.valueChanges
        .pipe(takeUntil(this._destroy))
        .subscribe(() => this.cd.markForCheck());
    }
  }

  private _updateFocusState() {
    // Usually the MDC foundation would call "activateFocus" and "deactivateFocus" whenever
    // certain DOM events are emitted. This is not possible in our implementation of the
    // form field because we support abstract form field controls which are not necessarily
    // of type input, nor do we have a reference to a native form field control element. Instead
    // we handle the focus by checking if the abstract form field control focused state changes.
    if (this.control.focused && !this._isFocused) {
      this._isFocused = true;
      // this._lineRipple?.activate();
    } else if (
      !this.control.focused &&
      (this._isFocused || this._isFocused === null)
    ) {
      this._isFocused = false;
      // this._lineRipple?.deactivate();
    }

    // this._textField?.nativeElement.classList.toggle(
    //   'mdc-text-field--focused',
    //   this._control.focused
    // );
  }

  /**
   * Determines whether a class from the AbstractControlDirective
   * should be forwarded to the host element.
   */
  _shouldForward(prop: string): boolean {
    const control = this.control
      ? this.control.ngControl || this.control
      : null;
    return control && control[prop as keyof typeof control];
  }
}
